import type { SelectProps } from "@mui/material";
import { Box, MenuItem, Select } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";
import type { FieldError } from "react-hook-form";

import { ExpandArrowIcon } from "Shared/Icons";
import { BlCheckboxInput } from "Shared/Inputs/BlCheckboxInput";
import type { BlOption } from "Shared/Inputs/BlOption";
import type { InputLabelProps } from "Shared/Inputs/InputLabel";
import { InputWrapper } from "Shared/Inputs/InputWrapper";

export type BlDropdownValueType = string | number | string[] | number[];

const EMPTY_SENTINEL = "__bl_empty__";

const DropdownIcon = () => (
  <Box
    sx={{
      position: "absolute",
      right: 12,
      pointerEvents: "none",
      display: "flex",
      alignItems: "center",
      color: "inherit",
    }}
  >
    <ExpandArrowIcon style={{ width: 12, height: "auto" }} />
  </Box>
);

export interface BlDropdownProps
  extends Omit<
    SelectProps,
    | "label"
    | "error"
    | "value"
    | "defaultValue"
    | "onChange"
    | "multiple"
    | "renderValue"
  > {
  options: BlOption[];
  value: BlDropdownValueType;
  defaultValue?: BlDropdownValueType;
  onChange?: (value: BlDropdownValueType) => void;
  multiple?: boolean;
  placeholder?: string;
  /** Text shown when all options are selected (multi-select only). */
  allSelectedText?: string;
  labelProps?: Omit<InputLabelProps, "htmlFor">;
  fieldError?: FieldError;
  fixedHeight?: boolean;
  sx?: SxProps<Theme>;
  /** When true, the selected empty option displays its label instead of showing as empty/placeholder. */
  showEmptyOptionLabel?: boolean;
}

export const BlDropdown = ({
  options,
  value,
  defaultValue,
  onChange,
  multiple,
  placeholder,
  allSelectedText,
  labelProps,
  fieldError,
  fixedHeight,
  disabled,
  name,
  sx,
  showEmptyOptionLabel,
  ...rest
}: BlDropdownProps): ReactNode => {
  const hasError = !!fieldError;

  const hasEmptyOption = options.some((opt) => opt.value === "");

  const internalValue = hasEmptyOption && value === "" ? EMPTY_SENTINEL : value;

  const internalOptions = hasEmptyOption
    ? options.map((opt) => ({
        ...opt,
        value: opt.value === "" ? EMPTY_SENTINEL : opt.value,
      }))
    : options;

  const isEmpty = (val: BlDropdownValueType): boolean => {
    if (!val || val === EMPTY_SENTINEL) return true;
    if (Array.isArray(val) && val.length === 0) return true;
    return false;
  };

  const toExternalValue = (val: BlDropdownValueType): BlDropdownValueType => {
    if (val === EMPTY_SENTINEL) return "";
    return val;
  };

  const renderValue = (val: unknown): React.ReactNode => {
    const typedVal = val as BlDropdownValueType;

    if (isEmpty(typedVal)) {
      if (showEmptyOptionLabel) {
        const emptyOption = internalOptions.find(
          (x) => x.value === EMPTY_SENTINEL,
        );
        if (emptyOption) {
          return (
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                ml: 1,
                mr: 2,
                textOverflow: "ellipsis",
                minHeight: 14,
              }}
            >
              {emptyOption.label}
            </Box>
          );
        }
      }
      return placeholder ? (
        <Box
          component="span"
          sx={{
            fontSize: 12,
            mr: 2,
            pl: 1,
            fontWeight: 400,
            color: "text.disabled",
          }}
        >
          {placeholder}
        </Box>
      ) : (
        <Box
          component="span"
          sx={{ overflow: "hidden", ml: 1, mr: 2, minHeight: 14 }}
        />
      );
    }

    if (
      multiple &&
      allSelectedText &&
      (typedVal as Array<string | number>).length === internalOptions.length
    ) {
      return (
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            ml: 1,
            mr: 2,
            textOverflow: "ellipsis",
            minHeight: 14,
          }}
        >
          {allSelectedText}
        </Box>
      );
    }

    const text = multiple
      ? internalOptions
          .filter((o) =>
            (typedVal as Array<string | number>).some((v) => v === o.value),
          )
          .map((x) => x.label)
          .join(", ")
      : internalOptions.find((x) => x.value.toString() === typedVal.toString())
          ?.label;

    return (
      <Box
        component="span"
        sx={{
          overflow: "hidden",
          ml: 1,
          mr: 2,
          textOverflow: "ellipsis",
          minHeight: 14,
        }}
      >
        {text}
      </Box>
    );
  };

  return (
    <InputWrapper
      labelProps={labelProps?.label ? { ...labelProps, disabled } : undefined}
      errorProps={{ error: fieldError, fixedHeight }}
    >
      <Select
        value={internalValue}
        defaultValue={defaultValue}
        onChange={(event) =>
          onChange?.(toExternalValue(event.target.value as BlDropdownValueType))
        }
        inputProps={{ "aria-label": labelProps?.label ?? placeholder ?? name }}
        IconComponent={DropdownIcon}
        disabled={disabled}
        multiple={multiple}
        error={hasError}
        fullWidth
        displayEmpty
        MenuProps={{ variant: "menu", style: { maxHeight: "300px" } }}
        renderValue={renderValue}
        variant="outlined"
        name={name}
        sx={sx}
        {...rest}
      >
        {internalOptions.map((opt) => (
          <MenuItem
            key={`${name}-${opt.value}`}
            value={opt.value}
            disabled={opt.disabled}
          >
            {multiple ? (
              <BlCheckboxInput
                checked={(internalValue as Array<string | number>).some(
                  (x) => x === opt.value,
                )}
                label={opt.label}
              />
            ) : (
              <span>{opt.label}</span>
            )}
          </MenuItem>
        ))}
      </Select>
    </InputWrapper>
  );
};
